import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader, Empty } from "@/components/PageHeader";
import { BoatForm } from "@/components/BoatForm";
import { api, errMsg, fmtDate } from "@/lib/api";

const CrewLine = ({ b }) => (
  <div className="flex items-center gap-2 text-sm">
    {b.crew_confermato ? <UserCheck className="h-4 w-4 text-emerald-600" /> : <Clock className="h-4 w-4 text-amber-600" />}
    <span className="font-medium">{b.crew_cognome} {b.crew_nome}</span>
    <span className="text-muted-foreground">({fmtDate(b.crew_data_nascita)})</span>
  </div>
);

const other = (r) => (r === "timoniere" ? "prodiere" : "timoniere");

const RoleLine = ({ b, crewView }) =>
  b.ruolo ? (
    <div className="text-xs text-muted-foreground mt-2" data-testid={`boat-role-${b.id}`}>
      Il tuo ruolo: <span className="font-medium capitalize text-slate-700">{crewView ? other(b.ruolo) : b.ruolo}</span>
    </div>
  ) : (
    !crewView && <div className="text-xs text-amber-700 mt-2" data-testid={`boat-role-missing-${b.id}`}>Ruolo a bordo non indicato: modifica la barca per impostarlo</div>
  );

const BoatCard = ({ b, onEdit, onDelete, crewView }) => (
  <div className="fade-up bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`boat-card-${b.id}`}>
    <div className="tricolore h-1" />
    <div className="p-6">
      <div className="flex items-start justify-between">
        <Badge className="bg-[#0B192C] font-heading uppercase tracking-wider text-sm">{b.categoria}</Badge>
        {!crewView && (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => onEdit(b)} data-testid={`boat-edit-button-${b.id}`}><Pencil className="h-4 w-4" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" data-testid={`boat-delete-button-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare la barca {b.numero_velico}?</AlertDialogTitle>
                  <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="boat-delete-cancel-button">Annulla</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(b)} className="bg-red-600 hover:bg-red-700" data-testid="boat-delete-confirm-button">Elimina</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
      <div className="font-mono-x text-4xl font-semibold tracking-wider text-[#0B192C] mt-4" data-testid={`boat-sail-number-${b.id}`}>{b.numero_velico}</div>
      <div className="text-xs font-mono-x uppercase tracking-wider text-muted-foreground mt-5 mb-1">{crewView ? "Armatore" : "Equipaggio"}</div>
      {crewView ? <div className="text-sm font-medium">{b.owner_cognome} {b.owner_nome}</div> : <CrewLine b={b} />}
      <RoleLine b={b} crewView={crewView} />
      {!crewView && !b.crew_confermato && <p className="text-xs text-amber-700 mt-2">In attesa che il membro si registri all'app</p>}
    </div>
  </div>
);

export default function Boats() {
  const [data, setData] = useState({ mine: [], crew: [] });
  const [form, setForm] = useState({ open: false, boat: null });

  const load = useCallback(() => api.get("/boats").then((r) => setData(r.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async (b) => {
    try {
      await api.delete(`/boats/${b.id}`);
      toast.success("Barca eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader kicker="La tua flotta" title="Barche">
        <Button onClick={() => setForm({ open: true, boat: null })} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="boat-add-button">
          <Plus className="h-4 w-4 mr-1" />Aggiungi barca
        </Button>
      </PageHeader>
      {data.mine.length === 0 ? (
        <Empty data-testid="boats-empty">Non hai ancora registrato imbarcazioni. Aggiungi categoria, numero velico e il secondo membro dell'equipaggio.</Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="boats-list">
          {data.mine.map((b) => <BoatCard key={b.id} b={b} onEdit={(x) => setForm({ open: true, boat: x })} onDelete={remove} />)}
        </div>
      )}
      {data.crew.length > 0 && (
        <>
          <h2 className="font-heading text-2xl uppercase mt-12 mb-4">Barche in cui sei equipaggio</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="boats-crew-list">
            {data.crew.map((b) => <BoatCard key={b.id} b={b} crewView />)}
          </div>
        </>
      )}
      <BoatForm open={form.open} boat={form.boat} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={load} />
    </div>
  );
}
